import { HIRE_ROUTES } from "plg/routes";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";

import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";
import Button from "components/common/Button";
import useWelcomeSequence, { WELCOME_LOADER_ROW, WELCOME_TOTAL_MS } from "plg/screens/Signup/useWelcomeSequence";
import usePlgSignupConfig from "plg/screens/Signup/usePlgSignupConfig";

import { fetchPlgMe } from "plg/api/plgAuth";
import { finishSetup } from "plg/api/plgSetup";
import { getPlgProfile, hasPlgSession, setPlgTokens } from "plg/auth/plgTokenStore";
import type { PlgTokens } from "plg/auth/plgTokenStore";
import { clearSignupSession, loadSignupSession } from "plg/utils/signupSession";
import { trackPlg } from "plg/analytics/events";
import { identifyPlg, setupTraits } from "plg/analytics/identity";
import { PLG_FEATURES } from "plg/features";
import PageLoader from "components/common/PageLoader";

import "./PlgWelcome.scss";
import { PLG_SIGNUP_ROUTE } from "constants/routes";

type Outcome = { kind: "pending" } | { kind: "ok"; data: PlgTokens } | { kind: "failed" };

function PlgWelcomePage() {
  const navigate = useNavigate();
  const { config } = usePlgSignupConfig();
  const { current, phase, beat, done, skip } = useWelcomeSequence();
  const [session] = useState(() => loadSignupSession());
  const [outcome, setOutcome] = useState<Outcome>({ kind: "pending" });
  const started = useRef(false);

  // Esc goes ahead like a click on the stage (prototype keydown handler).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [skip]);

  // The account already exists (the first step created it, later steps saved as they went): close setup.
  const submit = useCallback(async () => {
    setOutcome({ kind: "pending" });
    const res = await finishSetup();
    if (res.ok === true) {
      setOutcome({ kind: "ok", data: res.data });
      return;
    }
    trackPlg("error_shown", { error_type: res.code || "generic", screen: "account_setup" });
    if (res.status === 401) {
      clearSignupSession();
      navigate(`${PLG_SIGNUP_ROUTE}?expired=1`, { replace: true });
    } else setOutcome({ kind: "failed" });
  }, []);

  useEffect(() => {
    if (!hasPlgSession()) {
      navigate(`${PLG_SIGNUP_ROUTE}?expired=1`, { replace: true });
      return;
    }
    // StrictMode mounts effects twice in dev; the ref keeps this to one POST.
    if (started.current) return;
    started.current = true;
    submit();
  }, []);

  // Land in the PLG app only once the animation (or its skip) AND the request are both done.
  // Without the welcome transition the customer goes on as soon as setup is closed.
  const transition = PLG_FEATURES.welcomeTransition;
  const ready = done || !transition;

  useEffect(() => {
    if (!ready || outcome.kind !== "ok") return;
    const profile = setPlgTokens(outcome.data);
    if (!profile) {
      setOutcome({ kind: "failed" });
      return;
    }
    identifyPlg(profile, setupTraits(session?.answers || {}));
    fetchPlgMe().catch(() => undefined);
    clearSignupSession();
    navigate(HIRE_ROUTES.home, { replace: true });
  }, [ready, outcome]);

  const companyName =
    typeof session?.answers.companyName === "string" ? session.answers.companyName : getPlgProfile()?.companyName || "";
  const welcome = config?.welcome;
  const heading = (welcome?.heading || "You are in, {companyName}").replace("{companyName}", companyName);

  if (outcome.kind === "failed") {
    return (
      <div className="plg-welcome-error">
        <div className="plg-welcome-error__card">
          <EdgeLogo size="md" state="dark" />
          <h1>We could not finish setting up your account</h1>
          <p>Nothing was lost. Try again in a moment.</p>
          <Button variant="contained" size="large" label="Try again" onClick={submit} isFullWidth />
        </div>
      </div>
    );
  }

  if (!transition) return <PageLoader />;

  // Rows accumulate: a pulsing dot while current, a check once the next starts. The loader is the last row.
  // The timeline paces three status rows; any extra configured rows are ignored.
  const rows = (welcome?.rows || []).slice(0, WELCOME_LOADER_ROW);
  const rowState = (i: number) => (i === current ? " is-on" : i < current ? " is-done" : "");
  const mark = (
    <span className="wr-mark">
      <i />
      <CheckIcon />
    </span>
  );

  return (
    // Any click on the stage goes ahead now (prototype behaviour); navigation still waits for the request.
    <div className="wv wv-aperture" data-phase={phase} onClick={skip} role="presentation">
      <div className="ap-rise" />
      <div className="ap-lobe" />
      <div className="wl-end">
        <div className="lu-wash" />
      </div>
      <header className="wl-head">
        <div className="wl-logo">
          <EdgeLogo size="md" state={phase === 4 ? "dark" : "white"} />
        </div>
        <h1>{heading}</h1>
        <p className="wel-sub">{welcome?.sub}</p>
      </header>
      <div className="wl-block">
        <ol className="wl-list">
          {rows.map((row, i) => (
            <li className={`wr${rowState(i)}`} key={row}>
              {mark}
              <span className="wr-t">{row}</span>
            </li>
          ))}
          <li className={`wr wr-load${rowState(WELCOME_LOADER_ROW)}`} data-beat={beat}>
            {mark}
            <span className="wr-t">
              {(welcome?.loaderLines || []).map((line, i) => (
                <span data-b={i + 1} key={line}>
                  {line}
                </span>
              ))}
            </span>
          </li>
        </ol>
        <div className="wl-hair" style={{ "--dur": `${WELCOME_TOTAL_MS}ms` } as CSSProperties}>
          <i />
        </div>
      </div>
      <div className="wl-forward">
        <button type="button" className="wl-go" onClick={(e) => { e.stopPropagation(); skip(); }}>
          Continue <ArrowForwardIcon />
        </button>
      </div>
    </div>
  );
}

export default PlgWelcomePage;
